import { useMemo } from 'react';
import { TreeSelectPicker } from '@/components/forms/TreeSelectPicker';
import { adjacencyToHierarchyNodes } from '@/lib/treeLookup';
import { useOrgUnits } from '@/hooks/core/useOrgUnit';

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * Node ids to keep out of the picker entirely. Filtered before the projection below, so a node
   * whose only child is excluded also loses its expand chevron. Used by the move dialog to hide the
   * moved node and its own descendants — picking one of those would detach the subtree.
   */
  excludeIds?: ReadonlySet<string>;
}

/**
 * Org-unit picker that shows the org hierarchy as an expandable tree — the same component and behaviour
 * as the Plan-View "Root Node" picker (chevron expands/collapses without selecting; selection only on
 * clicking a node). Reuses TreeSelectPicker in data mode, fed the client-built OrgUnit tree.
 */
export function OrgUnitTreeSelect({
  value,
  onChange,
  label = 'Organisation unit',
  required,
  disabled,
  excludeIds,
}: Props) {
  const { data: orgUnits } = useOrgUnits();
  const nodes = useMemo(
    () => adjacencyToHierarchyNodes((orgUnits ?? []).filter((ou) => !excludeIds?.has(ou.id))),
    [orgUnits, excludeIds],
  );

  return (
    <TreeSelectPicker
      nodes={nodes}
      value={value}
      onChange={onChange}
      label={label}
      required={required}
      disabled={disabled}
    />
  );
}
